import { AppPlatform, AppStoreType, TimelineEventType } from './../types';
import { TimelineEvent, User } from '../types';
import { getIntegrationTypeForAppStoreType, getIsOTAPlatform } from '../helpers';
import { displayBetaServiceNameForAppStoreType } from '../helpers/displayHelpers';

export const generateTimelineEventsHistory: (args: {
    currentReleaseId?: string;
    version?: string;
    currentUser?: User;
    orgId?: string;
    appId?: string;
    appName?: string;
    platform?: AppPlatform;
    appStoreType?: AppStoreType;
    isReleaseComplete?: boolean;
}) => TimelineEvent[] = ({
    currentReleaseId,
    version,
    currentUser,
    orgId,
    appId,
    appName,
    platform,
    appStoreType,
    isReleaseComplete,
} = {}) => {
    const betaServiceName = appStoreType ? displayBetaServiceNameForAppStoreType(appStoreType) : '';
    const appStoreBuildIntegrationType = appStoreType ? getIntegrationTypeForAppStoreType(appStoreType) : '';

    const disallowedTimelineEventsForPlatform =
        getIsOTAPlatform(platform) ?
            [
                TimelineEventType.ReleaseSubmitted,
                TimelineEventType.ReleaseStepsReadyToSubmit,
                TimelineEventType.ReleaseTargetSubmissionDateUpdated,
                TimelineEventType.ReleaseStepMetadataApprovalChanged,
                TimelineEventType.ReleaseStepTranslationsApprovalChanged,
                TimelineEventType.ReleaseStepScreenshotAdded,
                TimelineEventType.ReleaseStepScreenshotDeleted,
                TimelineEventType.ReleaseStepScreenshotsApprovalChanged,
                TimelineEventType.RunwayEventReleaseStepAppPreviewAdded,
                TimelineEventType.RunwayEventReleaseStepAppPreviewDeleted,
                TimelineEventType.AppStoreVersionStatusUpdated,
                TimelineEventType.AppStoreVersionBetaSubmittedForReview,
                TimelineEventType.AppStoreVersionPhasedReleaseUpdated,
                TimelineEventType.AppStoreVersionReleaseNotesUpdated,
                TimelineEventType.AppStoreVersionAppStoreBuildChanged,
                TimelineEventType.ReleaseMarkedRejected,
                TimelineEventType.AndroidRolloutStartedManually,
                TimelineEventType.AndroidRolloutStartedFromForwardedEmail,
                TimelineEventType.BinaryArtifactAppStoreUploadSuccess,
                TimelineEventType.BinaryArtifactAppStoreUploadFailed,
                TimelineEventType.DsymArtifactStabilityMonitoringUploadSuccess,
                TimelineEventType.DsymArtifactStabilityMonitoringUploadFailed,
                TimelineEventType.ResignSequenceStarted,
                TimelineEventType.ResignSequenceCompleted,
                TimelineEventType.BuildResignStarted,
                TimelineEventType.BuildResignCompleted,
                TimelineEventType.SyncedVCSMetadata,
            ]
        :   [];

    const appStoreIntegration = platform === AppPlatform.Android ? 'google' : 'apple';
    const appStoreBuildNumber = platform === AppPlatform.Android ? '27500' : '298';
    const betaService = platform === AppPlatform.Android ? 'Google Play beta' : 'TestFlight';
    const ciBuildNumber = platform === AppPlatform.Android ? '275' : '298';

    const baseTimelineEvents = [
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: {
                appStoreBuildIntegrationType: appStoreIntegration,
                appStoreBuildNumber: appStoreBuildNumber,
                betaServiceName: betaService,
                ciBuildIntegrationType: 'github-ci',
                ciBuildNumber: ciBuildNumber,
                multipleBuildData: [
                    {
                        appStoreBuildNumber: appStoreBuildNumber,
                        ciBuildNumber: ciBuildNumber,
                    },
                ],
                regressionTestingStatus: 'passed',
            },
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: '2022-09-14T12:00:46.614459819Z',
            triggeredBy: 'user',
            type: 'releaseStep.regressionTestingStatusChanged',
            userId: 'sully@demo.com',
        },
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: null,
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: '2022-09-14T11:53:48.839978492Z',
            triggeredBy: 'user',
            type: 'appStoreVersion.releaseNotesUpdated',
            userId: 'sully@demo.com',
        },
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: {
                approved: true,
            },
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: '2022-09-14T11:53:43.45315196Z',
            triggeredBy: 'user',
            type: 'releaseStep.metadataApprovalChanged',
            userId: 'sully@demo.com',
        },
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: {
                appStoreBuildIntegrationType: appStoreIntegration,
                appStoreBuildNumber: appStoreBuildNumber,
                betaServiceName: betaService,
                multipleBuildData: [
                    {
                        appStoreBuildNumber: appStoreBuildNumber,
                        ciBuildNumber: ciBuildNumber,
                    },
                ],
            },
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: '2022-09-14T10:56:45.819357901Z',
            triggeredBy: 'runway',
            type: 'appStoreVersion.appStoreBuildChanged',
            userId: null,
        },
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: {
                artifactName: platform === AppPlatform.Android ? 'com.example.demo-release.aab' : 'appollo.ipa',
                destination: betaService,
            },
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: '2022-09-14T10:51:42.804806614Z',
            triggeredBy: 'runway',
            type: 'binaryArtifact.uploadSuccess',
            userId: null,
        },
        {
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            appId: appId ?? 'demo',
            eventData: {
                releaseStepStatus: 'ready',
                releaseStepType: 'releaseCandidate',
            },
            orgId: orgId ?? 'demo',
            releaseData: {
                displayableVersion: version ?? '1.0.0',
                version: version ?? '1.0.0',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            status: 'completed',
            triggeredAt: '2022-09-14T10:50:39.461796101Z',
            triggeredBy: 'unknown',
            type: 'releaseStep.statusChanged',
            userId: null,
        },
        {
            type: 'releaseStep.betaSoakStarted',
            triggeredAt: '2022-09-09T14:10:28.184149433Z',
            triggeredBy: 'unknown',
            userId: null,
            status: 'completed',
            eventData: {
                appStoreBuildIntegrationType: appStoreBuildIntegrationType,
                appStoreBuildNumber: appStoreBuildNumber,
                betaServiceName: betaServiceName,
                ciBuildIntegrationType: 'bitrise',
                ciBuildNumber: ciBuildNumber,
                multipleBuildData: [],
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
        },
        {
            type: 'releaseStep.rcBuildStatusChanged',
            triggeredAt: '2022-09-09T14:10:27.951707936Z',
            triggeredBy: 'unknown',
            userId: null,
            status: 'completed',
            eventData: {
                buildStatus: 'success',
                ciBuildIntegrationType: 'bitrise',
                ciBuildNumber: ciBuildNumber,
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
        },
        {
            type: 'releaseStep.statusChanged',
            triggeredAt: '2022-09-02T15:09:23.239093144Z',
            triggeredBy: 'unknown',
            userId: null,
            status: 'completed',
            eventData: {
                releaseStepStatus: 'pending',
                releaseStepType: 'betaTesting',
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
        },
        {
            type: 'releaseStep.statusChanged',
            triggeredAt: '2022-09-08T14:34:10.924379019Z',
            triggeredBy: 'unknown',
            userId: null,
            status: 'completed',
            eventData: {
                releaseStepStatus: 'pending',
                releaseStepType: 'regressionTesting',
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
        },
        {
            type: 'releaseStep.rcBuildStatusChanged',
            triggeredAt: '2022-09-02T14:58:13.492239257Z',
            triggeredBy: 'unknown',
            userId: null,
            status: 'completed',
            eventData: {
                buildStatus: 'inProgress',
                ciBuildIntegrationType: 'bitrise',
                ciBuildNumber: '275',
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
        },
        {
            type: 'release.branchCreated',
            triggeredAt: '2022-09-02T14:57:55.577883761Z',
            triggeredBy: 'runway',
            userId: null,
            status: 'completed',
            eventData: {
                releaseBranch: getIsOTAPlatform(platform) ? `codepush-${version}` : `release-${version}`,
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
        },
        {
            type: 'release.targetKickoffDateUpdated',
            triggeredAt: '2022-09-01T16:07:45.342682695Z',
            triggeredBy: 'user',
            userId: currentUser?.email ?? 'mjemison@demo.com',
            status: 'completed',
            eventData: {
                targetKickoffDate: '2022-09-02T15:00:00Z',
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
        },
        {
            type: 'releaseStep.statusChanged',
            triggeredAt: '2022-09-01T13:51:29.52915761Z',
            triggeredBy: 'unknown',
            userId: null,
            status: 'completed',
            eventData: {
                releaseStepStatus: 'pending',
                releaseStepType: 'approvals',
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
        },
        {
            type: 'release.targetKickoffDateUpdated',
            triggeredAt: '2022-08-30T17:58:09.247025021Z',
            triggeredBy: 'user',
            userId: 'mjemison@demo.com',
            status: 'completed',
            eventData: {
                targetKickoffDate: '2022-09-02T18:00:00Z',
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
        },
        {
            type: 'release.versionBumped',
            triggeredAt: '2022-08-17T17:58:40.737899875Z',
            triggeredBy: 'runway',
            userId: null,
            status: 'completed',
            eventData: {
                bumpedVersion: version ?? '1.0.0',
                branch: 'develop',
            },
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            appData: {
                appName: appName ?? 'Demo',
                platform: platform ?? 'ios',
            },
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
        },
        {
            type: 'release.created',
            triggeredAt: '2022-08-17T15:56:23.625387097Z',
            triggeredBy: 'runway',
            userId: null,
            status: 'completed',
            eventData: null,
            orgId: orgId ?? 'demo',
            appId: appId ?? 'demo',
            releaseId: currentReleaseId ?? '1.0.0',
            releaseData: {
                version: version ?? '1.0.0',
            },
        },
    ] as TimelineEvent[];

    if (isReleaseComplete) {
        const submitAndReleaseTimelineEvents = [
            {
                appData: {
                    appName: appName ?? 'Demo',
                    platform: platform ?? 'ios',
                },
                appId: appId ?? 'demo',
                eventData: {
                    appStoreBuildIntegrationType: appStoreIntegration,
                    appStoreBuildNumber: appStoreBuildNumber,
                    betaServiceName: betaService,
                    dayNumber: 0,
                    multipleBuildData: [
                        {
                            appStoreBuildNumber: appStoreBuildNumber,
                            ciBuildNumber: ciBuildNumber,
                        },
                    ],
                    userFraction: 1,
                },
                orgId: orgId ?? 'demo',
                releaseData: {
                    displayableVersion: version ?? '1.0.0',
                    version: version ?? '1.0.0',
                },
                releaseId: currentReleaseId ?? '1.0.0',
                status: 'completed',
                triggeredAt: '2022-09-19T21:10:46.348345153Z',
                triggeredBy: 'runway',
                type: 'appStoreVersion.phasedReleaseUpdated',
                userId: null,
            },
            {
                appData: {
                    appName: appName ?? 'Demo',
                    platform: platform ?? 'ios',
                },
                appId: appId ?? 'demo',
                eventData: {
                    vcsIntegration: 'github',
                },
                orgId: orgId ?? 'demo',
                releaseData: {
                    displayableVersion: version ?? '1.0.0',
                    version: version ?? '1.0.0',
                },
                releaseId: currentReleaseId ?? '1.0.0',
                status: 'completed',
                triggeredAt: '2022-09-16T21:03:22.709834352Z',
                triggeredBy: 'runway',
                type: 'release.tagged',
                userId: null,
            },
            {
                appData: {
                    appName: appName ?? 'Demo',
                    platform: platform ?? 'ios',
                },
                appId: appId ?? 'demo',
                eventData: {
                    appStoreBuildIntegrationType: appStoreIntegration,
                    appStoreBuildNumber: appStoreBuildNumber,
                    betaServiceName: betaService,
                    multipleBuildData: [
                        {
                            appStoreBuildNumber: appStoreBuildNumber,
                            ciBuildNumber: ciBuildNumber,
                        },
                    ],
                    reviewSubmissionItems: [],
                },
                orgId: orgId ?? 'demo',
                releaseData: {
                    displayableVersion: version ?? '1.0.0',
                    version: version ?? '1.0.0',
                },
                releaseId: currentReleaseId ?? '1.0.0',
                status: 'completed',
                triggeredAt: '2022-09-15T11:03:18.832064224Z',
                triggeredBy: 'runway',
                type: 'release.submitted',
                userId: null,
            },
            {
                appId: appId ?? 'demo',
                eventData: null,
                orgId: orgId ?? 'demo',
                releaseData: {
                    displayableVersion: version ?? '1.0.0',
                    version: version ?? '1.0.0',
                },
                releaseId: currentReleaseId ?? '1.0.0',
                status: 'completed',
                triggeredAt: '2022-09-15T03:15:54.535484007Z',
                triggeredBy: 'unknown',
                type: 'release.stepsReadyToSubmit',
                userId: null,
            },
        ] as TimelineEvent[];

        baseTimelineEvents.splice(0, 0, ...submitAndReleaseTimelineEvents);

        if (platform === AppPlatform.Ios) {
            const appStoreReviewTimelineEvents = [
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: {
                        releaseStepStatus: 'ready',
                        releaseStepType: 'takeoff',
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: '2022-09-15T23:40:54.535484007Z',
                    triggeredBy: 'unknown',
                    type: 'releaseStep.statusChanged',
                    userId: null,
                },
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: {
                        appStoreBuildIntegrationType: appStoreIntegration,
                        appStoreBuildNumber: appStoreBuildNumber,
                        appStoreStatus: 'READY_FOR_SALE',
                        betaServiceName: 'TestFlight',
                        multipleBuildData: [
                            {
                                appStoreBuildNumber: appStoreBuildNumber,
                                ciBuildNumber: ciBuildNumber,
                            },
                        ],
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: '2022-09-15T23:24:54.535484007Z',
                    triggeredBy: 'unknown',
                    type: 'appStoreVersion.statusUpdated',
                    userId: null,
                },
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: null,
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: '2022-09-15T23:23:54.535484007Z',
                    triggeredBy: 'unknown',
                    type: 'release.stepsReadyToRelease',
                    userId: null,
                },
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: {
                        appStoreBuildIntegrationType: appStoreIntegration,
                        appStoreBuildNumber: appStoreBuildNumber,
                        appStoreStatus: 'PENDING_DEVELOPER_RELEASE',
                        betaServiceName: 'TestFlight',
                        multipleBuildData: [
                            {
                                appStoreBuildNumber: appStoreBuildNumber,
                                ciBuildNumber: ciBuildNumber,
                            },
                        ],
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: '2022-09-15T23:20:54.535484007Z',
                    triggeredBy: 'unknown',
                    type: 'appStoreVersion.statusUpdated',
                    userId: null,
                },
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: {
                        appStoreBuildIntegrationType: appStoreIntegration,
                        appStoreBuildNumber: appStoreBuildNumber,
                        appStoreStatus: 'IN_REVIEW',
                        betaServiceName: 'TestFlight',
                        multipleBuildData: [
                            {
                                appStoreBuildNumber: appStoreBuildNumber,
                                ciBuildNumber: ciBuildNumber,
                            },
                        ],
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: '2022-09-15T23:19:54.535484007Z',
                    triggeredBy: 'unknown',
                    type: 'appStoreVersion.statusUpdated',
                    userId: null,
                },
                {
                    appData: {
                        appName: appName ?? 'Demo',
                        platform: platform ?? 'ios',
                    },
                    appId: appId ?? 'demo',
                    eventData: {
                        releaseStepStatus: 'pending',
                        releaseStepType: 'storeReview',
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: '2022-09-15T23:18:54.535484007Z',
                    triggeredBy: 'unknown',
                    type: 'releaseStep.statusChanged',
                    userId: null,
                },
                {
                    appId: appId ?? 'demo',
                    eventData: {
                        appStoreBuildIntegrationType: appStoreIntegration,
                        appStoreBuildNumber: appStoreBuildNumber,
                        appStoreStatus: 'WAITING_FOR_REVIEW',
                        betaServiceName: 'TestFlight',
                        multipleBuildData: [
                            {
                                appStoreBuildNumber: appStoreBuildNumber,
                                ciBuildNumber: ciBuildNumber,
                            },
                        ],
                    },
                    orgId: orgId ?? 'demo',
                    releaseData: {
                        version: version ?? '1.0.0',
                    },
                    releaseId: currentReleaseId ?? '1.0.0',
                    status: 'completed',
                    triggeredAt: '2022-09-15T23:17:54.535484007Z',
                    triggeredBy: 'unknown',
                    type: 'appStoreVersion.statusUpdated',
                    userId: null,
                },
            ] as TimelineEvent[];
            baseTimelineEvents.splice(3, 0, ...appStoreReviewTimelineEvents);
        }
    }

    return baseTimelineEvents.filter((event) => !disallowedTimelineEventsForPlatform.includes(event.type));
};
